import { __assign, __read } from 'tslib';
import { useRef, useState } from 'react';
import { useComposedRefs } from '../../hooks/useComposedRefs.js';
import '../../hooks/useDetectInputMethod.js';
import '@reach/auto-id';
import '@emotion/react';
import 'lodash.get';
import '@compono/ui-theme';
import 'deepmerge';
import '@emotion/is-prop-valid';
import { useUpdateEffect } from '../../hooks/useUpdateEffect/index.js';
import parseISO from '../../node_modules/date-fns/esm/parseISO/index.js';
import format from '../../node_modules/date-fns/esm/format/index.js';
import parse from '../../node_modules/date-fns/esm/parse/index.js';
import isSameDay from '../../node_modules/date-fns/esm/isSameDay/index.js';
import formatISO from '../../node_modules/date-fns/esm/formatISO/index.js';

var defaultDateFormat = "d/M/y";
var validateDate = function (date) { return !isNaN(Number(date)); };
var convertDateToInput = function (date, dateFormat) {
    if (dateFormat === void 0) { dateFormat = defaultDateFormat; }
    return date ? format(date, dateFormat) : "";
};
var convertDateToIso = function (date) {
    return date ? formatISO(date, { representation: "date" }) : "Invalid Date";
};
var convertInputToDate = function (value, dateFormat, dateRef) {
    if (dateFormat === void 0) { dateFormat = defaultDateFormat; }
    if (dateRef === void 0) { dateRef = new Date(0, 0, 0, 0, 0, 0); }
    return parse(value, dateFormat, dateRef);
};
var convertIsoToDate = function (value) {
    return parseISO(value);
};
var useDateSelectHandler = function (_a) {
    var _b = _a.inputFormat, inputFormat = _b === void 0 ? defaultDateFormat : _b, dismissOnSelect = _a.dismissOnSelect, onSelect = _a.onSelect, onChange = _a.onChange, datePickerValue = _a.datePickerValue, setInputValue = _a.setInputValue, setDatePickerValue = _a.setDatePickerValue;
    return function (_a) {
        var date = _a.date, setIsOpen = _a.setIsOpen;
        if (!(datePickerValue && isSameDay(date, datePickerValue))) {
            var value = convertDateToIso(date);
            var inputValue = convertDateToInput(date, inputFormat);
            setInputValue && setInputValue(inputValue);
            setDatePickerValue && setDatePickerValue(date);
            onSelect && onSelect(value);
            onChange && onChange(value);
        }
        dismissOnSelect && setIsOpen(false);
    };
};
var useInputKeyDownHandler = function (_a) {
    var focusDateRef = _a.focusDateRef, onKeyDown = _a.onKeyDown;
    return function (_a) {
        var isOpen = _a.isOpen, setIsOpen = _a.setIsOpen;
        return function (event) {
            onKeyDown && onKeyDown(event);
            if (event.isDefaultPrevented())
                return;
            switch (event.code) {
                case "ArrowDown":
                    if (!isOpen) {
                        setIsOpen(true);
                    }
                    setTimeout(function () {
                        var _a;
                        (_a = focusDateRef === null || focusDateRef === void 0 ? void 0 : focusDateRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    });
                    break;
                case "Escape":
                    isOpen && setIsOpen(false);
                    break;
            }
        };
    };
};
var useInputClickHandler = function (_a) {
    var onClick = _a.onClick;
    return function (_a) {
        var setIsOpen = _a.setIsOpen;
        return function (event) {
            onClick && onClick(event);
            if (event.isDefaultPrevented())
                return;
            !event.isDefaultPrevented() && setIsOpen(true);
        };
    };
};
var useInputChangeHandler = function (_a) {
    var onBlur = _a.onBlur, onChange = _a.onChange, _b = _a.inputFormat, inputFormat = _b === void 0 ? defaultDateFormat : _b, defaultDate = _a.defaultDate, datePickerValue = _a.datePickerValue, setInputValue = _a.setInputValue, setDatePickerValue = _a.setDatePickerValue;
    return function (event) {
        var inputValue = event.target.value.trim();
        setInputValue && setInputValue(inputValue);
        var date = convertInputToDate(inputValue, inputFormat, defaultDate);
        var isDateValid = validateDate(date);
        if (isDateValid && datePickerValue && isSameDay(date, datePickerValue))
            return;
        setDatePickerValue && setDatePickerValue(isDateValid ? date : undefined);
        var value = isDateValid ? convertDateToIso(date) : "Invalid Date";
        onChange && onChange(inputValue === "" ? "" : value);
        onBlur && onBlur(inputValue === "" ? "" : value);
    };
};
var useDatePickerInput = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.dismissOnSelect, dismissOnSelect = _c === void 0 ? true : _c, defaultValue = _b.defaultValue, value = _b.value, _d = _b.inputFormat, inputFormat = _d === void 0 ? defaultDateFormat : _d, ref = _b.ref, size = _b.size, disabled = _b.disabled, readOnly = _b.readOnly, onSelect = _b.onSelect, onChange = _b.onChange, onClick = _b.onClick, onBlur = _b.onBlur, onKeyDown = _b.onKeyDown;
    var focusDateRef = useRef(null);
    var defaultDate = convertIsoToDate(value || defaultValue || "");
    var _e = __read(useState(validateDate(defaultDate)
        ? convertDateToInput(defaultDate, inputFormat)
        : ""), 2), inputValue = _e[0], setInputValue = _e[1];
    var _f = __read(useState(validateDate(defaultDate) ? defaultDate : undefined), 2), datePickerValue = _f[0], setDatePickerValue = _f[1];
    var returnValues = {
        dismissOnSelect: dismissOnSelect,
        defaultValue: defaultValue,
        inputFormat: inputFormat,
        onSelect: onSelect,
        onChange: onChange,
        onClick: onClick,
        onBlur: onBlur,
        onKeyDown: onKeyDown,
        focusDateRef: focusDateRef,
        inputValue: inputValue,
        setInputValue: setInputValue,
        datePickerValue: datePickerValue,
        setDatePickerValue: setDatePickerValue,
    };
    var getInputProps = function (menuContext) {
        var id = menuContext.id, triggerRef = menuContext.triggerRef, anchorRef = menuContext.anchorRef;
        var combinedRef = ref ? useComposedRefs(ref, triggerRef) : triggerRef;
        return {
            onKeyDown: useInputKeyDownHandler(returnValues)(menuContext),
            onClick: useInputClickHandler(returnValues)(menuContext),
            onChange: useInputChangeHandler(__assign(__assign({}, returnValues), { onBlur: undefined })),
            onBlur: useInputChangeHandler(__assign(__assign({}, returnValues), { onChange: undefined })),
            value: inputValue,
            placeholder: "Select a date",
            "aria-controls": "".concat(id, "-dropdown"),
            id: id,
            ref: combinedRef,
            containerRef: anchorRef,
            size: size,
            disabled: disabled,
            readOnly: readOnly,
            autoComplete: "off",
        };
    };
    var getContentProps = function (_a) {
        var id = _a.id;
        return ({
            value: datePickerValue,
            onSelect: useDateSelectHandler(returnValues),
            defaultFocusDate: new Date(),
            focusDateRef: focusDateRef,
            autoFocus: false,
            size: size,
            dropdownContentProp: {
                focusLockProps: {
                    autoFocus: false,
                    group: "focus-lock-group--".concat(id),
                },
            },
        });
    };
    var getFocusLockProps = function (_a) {
        var id = _a.id, isOpen = _a.isOpen;
        return ({
            autoFocus: false,
            group: "focus-lock-group--".concat(id),
            disabled: !isOpen,
        });
    };
    useUpdateEffect(function () {
        if (!value) {
            setInputValue("");
            setDatePickerValue(undefined);
            return;
        }
        var date = convertIsoToDate(value || "");
        if (datePickerValue && isSameDay(date, datePickerValue))
            return;
        if (validateDate(date)) {
            setInputValue(convertDateToInput(date, inputFormat));
            setDatePickerValue(date);
        }
    }, [value]);
    return __assign(__assign({}, returnValues), { getContentProps: getContentProps, getFocusLockProps: getFocusLockProps, getInputProps: getInputProps });
};

export { useDatePickerInput };
//# sourceMappingURL=useDatePickerInput.js.map
